import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { useAuthStore } from '../store/authStore';
import { Search, Lock, ArrowUpDown, ChevronUp, ChevronDown, Brain, BarChart3, Target, Award } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || 'http://64.23.240.10/api/v1';
const BASE_API = API.replace('/v1', '');

function formatTVL(val: number | string | null | undefined): string {
  if (val == null) return 'N/A';
  const n = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(n)) return 'N/A';
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
}

function tierBadgeColor(tier: string): string {
  switch (tier) {
    case 'Tier 1': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    case 'Tier 2': return 'bg-green-500/20 text-green-400 border-green-500/30';
    case 'Tier 3': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    case 'Tier 4': return 'bg-red-500/20 text-red-400 border-red-500/30';
    default: return 'bg-sky-blue/20 text-sky-blue border-sky-blue/30';
  }
}

function gradeBadgeColor(grade: string): string {
  if (!grade) return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  const g = grade.charAt(0);
  switch (g) {
    case 'A': return 'bg-green-500/20 text-green-400 border-green-500/30';
    case 'B': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    case 'C': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    case 'D': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
    case 'F': return 'bg-red-500/20 text-red-400 border-red-500/30';
    default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  }
}

function riskScoreColor(score: number): string {
  if (score >= 75) return 'text-green-400';
  if (score >= 50) return 'text-yellow-400';
  if (score >= 25) return 'text-orange-400';
  return 'text-red-400';
}

function StabilityBar({ score }: { score: number }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-navy-dark/50 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${score >= 70 ? 'bg-green-400' : score >= 40 ? 'bg-yellow-400' : 'bg-red-400'}`}
          style={{ width: `${Math.min(100, score)}%` }}
        />
      </div>
      <span className="text-xs text-cream font-medium w-8">{score}</span>
    </div>
  );
}

type SortField = 'name' | 'chain' | 'apy' | 'tvl' | 'tvl_tier' | 'stability_score' | 'risk_score' | 'grade';

export default function IntelligencePage() {
  const { isAuthenticated, token, user } = useAuthStore();
  const tier = user?.subscriptionTier || 'free';
  const isPro = tier === 'pro' || tier === 'premium';

  const [activeTab, setActiveTab] = useState<'all' | 'sweet-spots' | 'risk-leaders'>('all');
  const [overview, setOverview] = useState<any>(null);
  const [protocols, setProtocols] = useState<any[]>([]);
  const [sweetSpots, setSweetSpots] = useState<any[]>([]);
  const [riskLeaders, setRiskLeaders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [chainFilter, setChainFilter] = useState('All');
  const [tierFilter, setTierFilter] = useState('All');
  const [riskFilter, setRiskFilter] = useState('All');
  const [sortField, setSortField] = useState<SortField>('tvl');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  useEffect(() => {
    setLoading(true);
    setError('');
    Promise.all([
      fetch(`${BASE_API}/intelligence/overview`, { headers }).then(r => r.json()).catch(() => null),
      fetch(`${BASE_API}/protocols/all`, { headers }).then(r => r.json()).catch(() => ({ data: [] })),
    ]).then(([ov, prots]) => {
      setOverview(ov);
      const list = prots?.data || prots?.protocols || prots || [];
      setProtocols(Array.isArray(list) ? list : []);
      setLoading(false);
    }).catch(() => { setError('Failed to load intelligence data'); setLoading(false); });
  }, [token]);

  useEffect(() => {
    if (activeTab === 'sweet-spots' && isPro && sweetSpots.length === 0) {
      fetch(`${BASE_API}/intelligence/sweet-spots`, { headers }).then(r => r.json()).then(d => {
        setSweetSpots(d?.data || d?.sweetSpots || d || []);
      }).catch(() => {});
    }
    if (activeTab === 'risk-leaders' && isPro && riskLeaders.length === 0) {
      fetch(`${BASE_API}/intelligence/risk-leaders`, { headers }).then(r => r.json()).then(d => {
        setRiskLeaders(d?.data || d?.leaders || d || []);
      }).catch(() => {});
    }
  }, [activeTab, isPro]);

  const chains = useMemo(() => {
    const set = new Set<string>();
    protocols.forEach(p => { if (p.chain) set.add(p.chain); });
    return ['All', ...Array.from(set).sort()];
  }, [protocols]);

  const filteredProtocols = useMemo(() => {
    let result = [...protocols];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p => (p.name || '').toLowerCase().includes(q) || (p.chain || '').toLowerCase().includes(q) || (p.slug || '').toLowerCase().includes(q));
    }
    if (chainFilter !== 'All') result = result.filter(p => p.chain === chainFilter);
    if (tierFilter !== 'All') result = result.filter(p => (p.tvl_tier || p.tvlTier || '') === tierFilter);
    if (riskFilter !== 'All') result = result.filter(p => (p.risk_classification || p.riskClassification || '').toLowerCase() === riskFilter.toLowerCase());

    result.sort((a, b) => {
      let va: any, vb: any;
      switch (sortField) {
        case 'name': va = (a.name || '').toLowerCase(); vb = (b.name || '').toLowerCase(); break;
        case 'chain': va = (a.chain || '').toLowerCase(); vb = (b.chain || '').toLowerCase(); break;
        case 'apy': va = parseFloat(a.current_apy || a.apy || 0); vb = parseFloat(b.current_apy || b.apy || 0); break;
        case 'tvl': va = parseFloat(a.tvl || 0); vb = parseFloat(b.tvl || 0); break;
        case 'tvl_tier': va = a.tvl_tier || a.tvlTier || 'Tier 9'; vb = b.tvl_tier || b.tvlTier || 'Tier 9'; break;
        case 'stability_score': va = a.stability_score ?? a.stabilityScore ?? 0; vb = b.stability_score ?? b.stabilityScore ?? 0; break;
        case 'risk_score': va = a.risk_score ?? a.riskScore ?? 0; vb = b.risk_score ?? b.riskScore ?? 0; break;
        case 'grade': va = a.grade || 'Z'; vb = b.grade || 'Z'; break;
        default: va = 0; vb = 0;
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [protocols, searchQuery, chainFilter, tierFilter, riskFilter, sortField, sortDir]);

  const handleSort = (field: SortField) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 opacity-40" />;
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3 text-neon-red" /> : <ChevronDown className="w-3 h-3 text-neon-red" />;
  };

  const totalProtocols = overview?.totalProtocols || protocols.length || 156;
  const totalTVL = overview?.totalTVL || overview?.tvlTracked;
  const avgAPY = overview?.avgAPY || overview?.averageAPY;
  const tierDist = overview?.tierDistribution || overview?.tiers || {};

  if (loading) return (
    <div className="min-h-screen bg-navy-dark flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin w-12 h-12 border-4 border-neon-red border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-sky-blue">Loading intelligence data...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-navy-dark py-12">
      <SEO
        title="Protocol Intelligence Dashboard | ResidualVault"
        description="Real-time risk scoring, TVL analysis, and yield intelligence across 156+ DeFi protocols. Discover sweet spots, grade protocols, and track stability."
        canonical="https://www.residualvault.com/intelligence"
        keywords="DeFi intelligence, protocol risk scoring, TVL analysis, yield intelligence, staking analytics"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Hero */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-neon-red/10 border border-neon-red/30 rounded-full text-neon-red text-sm font-semibold mb-4">
            <Brain className="w-4 h-4" /> Protocol Intelligence
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="text-cream">Protocol Intelligence </span>
            <span className="gradient-text-neon">Dashboard</span>
          </h1>
          <p className="text-xl text-sky-blue max-w-3xl mx-auto">
            Real-time risk scoring, TVL analysis, and yield intelligence across {totalProtocols}+ protocols
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="card-glass text-center">
            <div className="text-sky-blue text-xs uppercase tracking-wider mb-1">Protocols Tracked</div>
            <div className="text-3xl font-bold gradient-text-neon">{totalProtocols}</div>
          </div>
          <div className="card-glass text-center">
            <div className="text-sky-blue text-xs uppercase tracking-wider mb-1">TVL Tracked</div>
            <div className="text-3xl font-bold gradient-text-neon">{totalTVL ? formatTVL(totalTVL) : '$61.8B'}</div>
          </div>
          <div className="card-glass text-center">
            <div className="text-sky-blue text-xs uppercase tracking-wider mb-1">Average APY</div>
            <div className="text-3xl font-bold gradient-text-neon">{avgAPY ? `${parseFloat(avgAPY).toFixed(1)}%` : '7.2%'}</div>
          </div>
          <div className="card-glass text-center">
            <div className="text-sky-blue text-xs uppercase tracking-wider mb-1">Tier Distribution</div>
            <div className="flex items-center justify-center gap-2 mt-1">
              {['Tier 1', 'Tier 2', 'Tier 3', 'Tier 4'].map(t => (
                <span key={t} className={`px-2 py-0.5 rounded text-xs font-medium border ${tierBadgeColor(t)}`}>
                  {t.replace('Tier ', 'T')}: {tierDist[t] || tierDist[t.replace('Tier ', 'tier')] || '-'}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1 p-1 bg-steel-blue/10 rounded-xl mb-8 border border-sky-blue/10">
          {[
            { key: 'all' as const, label: 'All Protocols', icon: BarChart3 },
            { key: 'sweet-spots' as const, label: 'Sweet Spots', icon: Target },
            { key: 'risk-leaders' as const, label: 'Risk Leaders', icon: Award },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${activeTab === tab.key ? 'bg-gradient-cta text-white shadow-neon' : 'text-sky-blue hover:text-cream hover:bg-steel-blue/20'}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.key !== 'all' && !isPro && (
                <Lock className="w-3 h-3 opacity-60" />
              )}
            </button>
          ))}
        </div>

        {error && (
          <div className="card-premium text-center py-8 mb-8">
            <p className="text-neon-red">{error}</p>
          </div>
        )}

        {/* All Protocols Tab */}
        {activeTab === 'all' && (
          <div>
            {/* Filters */}
            <div className="card-premium mb-6">
              <div className="flex flex-wrap gap-3">
                <div className="flex-1 min-w-56 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-sky-blue" />
                  <input
                    type="text"
                    placeholder="Search protocols..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 bg-navy-dark border border-sky-blue/30 rounded-lg text-cream placeholder-sky-blue/50 focus:outline-none focus:border-neon-red transition-colors"
                  />
                </div>
                <select value={chainFilter} onChange={e => setChainFilter(e.target.value)} className="px-4 py-2.5 bg-navy-dark border border-sky-blue/30 rounded-lg text-cream focus:outline-none focus:border-neon-red">
                  {chains.map(c => <option key={c} value={c} className="bg-navy-dark">{c === 'All' ? 'All Chains' : c}</option>)}
                </select>
                <select value={tierFilter} onChange={e => setTierFilter(e.target.value)} className="px-4 py-2.5 bg-navy-dark border border-sky-blue/30 rounded-lg text-cream focus:outline-none focus:border-neon-red">
                  <option value="All" className="bg-navy-dark">All Tiers</option>
                  <option value="Tier 1" className="bg-navy-dark">Tier 1 — Established</option>
                  <option value="Tier 2" className="bg-navy-dark">Tier 2 — Growth</option>
                  <option value="Tier 3" className="bg-navy-dark">Tier 3 — Emerging</option>
                  <option value="Tier 4" className="bg-navy-dark">Tier 4 — Early</option>
                </select>
                <select value={riskFilter} onChange={e => setRiskFilter(e.target.value)} className="px-4 py-2.5 bg-navy-dark border border-sky-blue/30 rounded-lg text-cream focus:outline-none focus:border-neon-red">
                  <option value="All" className="bg-navy-dark">All Risk Levels</option>
                  <option value="low" className="bg-navy-dark">Low Risk</option>
                  <option value="moderate" className="bg-navy-dark">Moderate Risk</option>
                  <option value="high" className="bg-navy-dark">High Risk</option>
                  <option value="very high" className="bg-navy-dark">Very High Risk</option>
                </select>
              </div>
            </div>

            {/* Table */}
            <div className="card-premium overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-sky-blue/20">
                      {[
                        { field: 'name' as SortField, label: 'Protocol' },
                        { field: 'chain' as SortField, label: 'Chain' },
                        { field: 'apy' as SortField, label: 'APY' },
                        { field: 'tvl' as SortField, label: 'TVL' },
                        { field: 'tvl_tier' as SortField, label: 'TVL Tier' },
                        { field: 'stability_score' as SortField, label: 'Stability' },
                        { field: 'risk_score' as SortField, label: 'Risk Score' },
                        { field: 'grade' as SortField, label: 'Grade' },
                      ].map(col => (
                        <th
                          key={col.field}
                          onClick={() => handleSort(col.field)}
                          className="text-left py-3 px-4 font-semibold text-sky-blue text-sm cursor-pointer hover:text-cream transition-colors whitespace-nowrap"
                        >
                          <div className="flex items-center gap-1">
                            {col.label}
                            <SortIcon field={col.field} />
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProtocols.map((p, i) => {
                      const apy = parseFloat(p.current_apy || p.apy || 0);
                      const tvl = p.tvl;
                      const tvlTier = p.tvl_tier || p.tvlTier || '';
                      const tvlLabel = p.tvl_tier_label || p.tvlTierLabel || '';
                      const stability = p.stability_score ?? p.stabilityScore;
                      const riskScore = p.risk_score ?? p.riskScore;
                      const grade = p.grade || '';
                      const showGated = !isAuthenticated;
                      const showFreeGate = isAuthenticated && tier === 'free';

                      return (
                        <tr key={p.id || p.slug || i} className="border-b border-sky-blue/10 hover:bg-steel-blue/5 transition-colors">
                          <td className="py-3 px-4">
                            <Link to={`/protocol/${p.slug}`} className="text-cream font-medium hover:text-neon-red transition-colors">
                              {p.name}
                            </Link>
                          </td>
                          <td className="py-3 px-4">
                            <span className="px-2 py-0.5 bg-steel-blue/20 border border-sky-blue/30 rounded text-xs text-sky-blue">{p.chain}</span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="font-bold gradient-text-neon">{apy.toFixed(2)}%</span>
                          </td>

                          {/* TVL - visible to auth */}
                          <td className="py-3 px-4">
                            {showGated ? (
                              <span className="text-steel-blue/40 blur-sm select-none">$1.2B</span>
                            ) : (
                              <span className="text-cream font-medium">{formatTVL(tvl)}</span>
                            )}
                          </td>

                          {/* TVL Tier - visible to free+ */}
                          <td className="py-3 px-4">
                            {showGated ? (
                              <span className="text-steel-blue/40 blur-sm select-none">Tier 1</span>
                            ) : (
                              <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${tierBadgeColor(tvlTier)}`}>
                                {tvlTier}{tvlLabel ? ` — ${tvlLabel}` : ''}
                              </span>
                            )}
                          </td>

                          {/* Stability - starter+ */}
                          <td className="py-3 px-4 min-w-[140px]">
                            {showGated || showFreeGate ? (
                              <div className="relative">
                                <div className="blur-sm select-none"><StabilityBar score={72} /></div>
                                {showGated && <span className="absolute inset-0 flex items-center justify-center text-xs text-neon-red font-medium"><Lock className="w-3 h-3 mr-1" />Sign up</span>}
                                {showFreeGate && <span className="absolute inset-0 flex items-center justify-center text-xs text-neon-red font-medium"><Lock className="w-3 h-3 mr-1" />Starter</span>}
                              </div>
                            ) : (
                              <StabilityBar score={stability ?? 0} />
                            )}
                          </td>

                          {/* Risk Score - pro+ */}
                          <td className="py-3 px-4">
                            {!isPro ? (
                              <div className="relative">
                                <span className="blur-sm select-none text-yellow-400 font-bold">65</span>
                                <span className="absolute inset-0 flex items-center text-xs text-neon-red font-medium"><Lock className="w-3 h-3 mr-1" />Pro</span>
                              </div>
                            ) : (
                              <span className={`font-bold ${riskScoreColor(riskScore ?? 0)}`}>{riskScore ?? '-'}</span>
                            )}
                          </td>

                          {/* Grade - free+ */}
                          <td className="py-3 px-4">
                            {showGated ? (
                              <div className="relative">
                                <span className="blur-sm select-none text-green-400 font-bold">B+</span>
                                <span className="absolute inset-0 flex items-center text-xs text-neon-red font-medium"><Lock className="w-3 h-3 mr-1" />Free</span>
                              </div>
                            ) : (
                              <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold border ${gradeBadgeColor(grade)}`}>{grade || '-'}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {filteredProtocols.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-sky-blue">No protocols match your filters.</p>
                </div>
              )}
            </div>

            {/* Non-auth CTA */}
            {!isAuthenticated && (
              <div className="mt-8 card-premium text-center">
                <Lock className="w-8 h-8 text-neon-red mx-auto mb-3" />
                <h3 className="text-xl font-bold text-cream mb-2">Unlock Protocol Intelligence</h3>
                <p className="text-sky-blue mb-4">Sign up free to see grades, TVL tiers, and more. Upgrade for full risk scoring and stability analysis.</p>
                <div className="flex gap-3 justify-center flex-wrap">
                  <Link to="/register" className="btn-neon">Create Free Account</Link>
                  <Link to="/pricing" className="bg-steel-blue/20 border border-sky-blue/30 text-sky-blue font-semibold px-6 py-3 rounded-lg hover:bg-steel-blue/30 transition-all">View Plans</Link>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Sweet Spots Tab */}
        {activeTab === 'sweet-spots' && (
          <div>
            {!isPro ? (
              <div className="card-premium">
                <div className="text-center mb-8">
                  <Target className="w-12 h-12 text-neon-red mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-cream mb-3">Sweet Spot Scanner</h2>
                  <p className="text-sky-blue max-w-2xl mx-auto mb-6">
                    The Sweet Spot Scanner identifies protocols where TVL is credible but yields have not compressed yet —
                    the best risk-adjusted opportunities in DeFi. Upgrade to Pro ($9.99/mo) to unlock.
                  </p>
                </div>
                {/* Blurred teaser */}
                <div className="space-y-3 mb-8">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="flex items-center justify-between p-4 bg-navy-dark/50 rounded-xl border border-sky-blue/10 blur-sm select-none">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-cta rounded-lg" />
                        <div>
                          <div className="text-cream font-medium">Protocol Name</div>
                          <div className="text-sky-blue text-sm">Ethereum</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-center"><div className="text-xs text-sky-blue">Sweet Spot</div><div className="text-lg font-bold text-green-400">87</div></div>
                        <div className="text-center"><div className="text-xs text-sky-blue">APY</div><div className="text-lg font-bold gradient-text-neon">12.4%</div></div>
                        <div className="text-center"><div className="text-xs text-sky-blue">TVL</div><div className="text-lg font-bold text-cream">$234M</div></div>
                        <div className="text-center"><div className="text-xs text-sky-blue">Risk</div><div className="text-lg font-bold text-yellow-400">45</div></div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="text-center">
                  <Link to="/pricing#pro" className="btn-neon inline-flex items-center gap-2">
                    <Lock className="w-4 h-4" /> Upgrade to Pro — $9.99/mo
                  </Link>
                </div>
              </div>
            ) : (
              <div className="card-premium">
                <div className="flex items-center gap-3 mb-6">
                  <Target className="w-6 h-6 text-neon-red" />
                  <h2 className="text-2xl font-bold text-cream">Sweet Spot Protocols</h2>
                </div>
                <div className="space-y-3">
                  {(Array.isArray(sweetSpots) ? sweetSpots : []).map((p: any, i: number) => (
                    <Link key={p.slug || i} to={`/protocol/${p.slug}`} className="flex items-center justify-between p-4 bg-navy-dark/50 rounded-xl border border-sky-blue/10 hover:border-neon-red/30 transition-all group">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-cta rounded-lg flex items-center justify-center text-white font-bold">{(p.name || '?')[0]}</div>
                        <div>
                          <div className="text-cream font-medium group-hover:text-neon-red transition-colors">{p.name}</div>
                          <div className="text-sky-blue text-sm">{p.chain}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-center"><div className="text-xs text-sky-blue">Sweet Spot</div><div className="text-lg font-bold text-green-400">{p.sweet_spot_score ?? p.sweetSpotScore ?? '-'}</div></div>
                        <div className="text-center"><div className="text-xs text-sky-blue">APY</div><div className="text-lg font-bold gradient-text-neon">{parseFloat(p.current_apy || p.apy || 0).toFixed(1)}%</div></div>
                        <div className="text-center"><div className="text-xs text-sky-blue">TVL</div><div className="text-lg font-bold text-cream">{formatTVL(p.tvl)}</div></div>
                        <div className="text-center"><div className="text-xs text-sky-blue">Risk</div><div className={`text-lg font-bold ${riskScoreColor(p.risk_score ?? p.riskScore ?? 0)}`}>{p.risk_score ?? p.riskScore ?? '-'}</div></div>
                        <div className="text-center"><div className="text-xs text-sky-blue">Stability</div><div className="text-lg font-bold text-blue-400">{p.stability_score ?? p.stabilityScore ?? '-'}</div></div>
                      </div>
                    </Link>
                  ))}
                  {sweetSpots.length === 0 && (
                    <div className="text-center py-8 text-sky-blue">Loading sweet spot data...</div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Risk Leaders Tab */}
        {activeTab === 'risk-leaders' && (
          <div>
            {!isPro ? (
              <div className="card-premium">
                <div className="text-center mb-8">
                  <Award className="w-12 h-12 text-neon-red mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-cream mb-3">Risk Leaders</h2>
                  <p className="text-sky-blue max-w-2xl mx-auto mb-6">
                    Risk Leaders ranks the safest protocols by composite risk score, factoring in TVL stability,
                    APY volatility, and smart contract maturity. Upgrade to Pro ($9.99/mo) to unlock.
                  </p>
                </div>
                <div className="space-y-3 mb-8">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="flex items-center justify-between p-4 bg-navy-dark/50 rounded-xl border border-sky-blue/10 blur-sm select-none">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center text-green-400 font-bold text-sm">#{i}</div>
                        <div className="w-10 h-10 bg-gradient-cta rounded-lg" />
                        <div>
                          <div className="text-cream font-medium">Protocol Name</div>
                          <div className="text-sky-blue text-sm">Ethereum</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-center"><div className="text-xs text-sky-blue">Risk Score</div><div className="text-lg font-bold text-green-400">92</div></div>
                        <div className="text-center"><div className="text-xs text-sky-blue">Classification</div><div className="text-sm font-medium text-green-400">Low Risk</div></div>
                        <div className="text-center"><div className="text-xs text-sky-blue">Stability</div><div className="text-lg font-bold text-blue-400">88</div></div>
                        <div className="text-center"><div className="text-xs text-sky-blue">TVL Tier</div><div className="text-sm font-medium text-blue-400">Tier 1</div></div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="text-center">
                  <Link to="/pricing#pro" className="btn-neon inline-flex items-center gap-2">
                    <Lock className="w-4 h-4" /> Upgrade to Pro — $9.99/mo
                  </Link>
                </div>
              </div>
            ) : (
              <div className="card-premium">
                <div className="flex items-center gap-3 mb-6">
                  <Award className="w-6 h-6 text-neon-red" />
                  <h2 className="text-2xl font-bold text-cream">Safest Protocols by Risk Score</h2>
                </div>
                <div className="space-y-3">
                  {(Array.isArray(riskLeaders) ? riskLeaders : []).map((p: any, i: number) => (
                    <Link key={p.slug || i} to={`/protocol/${p.slug}`} className="flex items-center justify-between p-4 bg-navy-dark/50 rounded-xl border border-sky-blue/10 hover:border-neon-red/30 transition-all group">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center text-green-400 font-bold text-sm">#{i + 1}</div>
                        <div className="w-10 h-10 bg-gradient-cta rounded-lg flex items-center justify-center text-white font-bold">{(p.name || '?')[0]}</div>
                        <div>
                          <div className="text-cream font-medium group-hover:text-neon-red transition-colors">{p.name}</div>
                          <div className="text-sky-blue text-sm">{p.chain}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-center"><div className="text-xs text-sky-blue">Risk Score</div><div className={`text-lg font-bold ${riskScoreColor(p.risk_score ?? p.riskScore ?? 0)}`}>{p.risk_score ?? p.riskScore ?? '-'}</div></div>
                        <div className="text-center"><div className="text-xs text-sky-blue">Classification</div><div className={`text-sm font-medium ${(p.risk_classification || '').toLowerCase().includes('low') ? 'text-green-400' : 'text-yellow-400'}`}>{p.risk_classification || p.riskClassification || '-'}</div></div>
                        <div className="text-center"><div className="text-xs text-sky-blue">Stability</div><div className="text-lg font-bold text-blue-400">{p.stability_score ?? p.stabilityScore ?? '-'}</div></div>
                        <div className="text-center"><div className="text-xs text-sky-blue">TVL Tier</div><span className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${tierBadgeColor(p.tvl_tier || p.tvlTier || '')}`}>{p.tvl_tier || p.tvlTier || '-'}</span></div>
                      </div>
                    </Link>
                  ))}
                  {riskLeaders.length === 0 && (
                    <div className="text-center py-8 text-sky-blue">Loading risk leader data...</div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
